/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import './style/bolmateSectionCard.scss';

import classNames from 'classnames';

import React, {ReactNode} from 'react';

import {DotsLoader} from '../loader/dotsLoader';

import BolmateButton from '../button/bolmateButton';

import BolmateEmptyState from '../emptyState/bolmateEmptyState';

import BolmateListGroup from '../listGroup/bolmateListGroup';

interface Props {
    /** Centered pill label above the rows. */
    title: ReactNode;
    /** First load — replaces the rows with a loader. */
    loading?: boolean;
    /** No rows to show. Ignored while loading. */
    empty?: boolean;
    emptyLabel?: ReactNode;
    /** Request failed — takes precedence over the empty state. */
    error?: ReactNode;
    hasMore?: boolean;
    /** A follow-up page is on its way; the button reports it instead of the rows. */
    loadingMore?: boolean;
    loadMoreLabel?: ReactNode;
    onLoadMore?: () => void;
    className?: string;
    children: ReactNode;
}

/**
 * Framed section of a dashboard-style page: a titled group of rows with an
 * optional "load more" footer, plus the loading / empty / error states every
 * such section needs.
 */
export default function BolmateSectionCard(props: Props) {
    const {
        title,
        loading,
        empty,
        emptyLabel,
        error,
        hasMore,
        loadingMore,
        loadMoreLabel,
        onLoadMore,
        className,
        children
    } = props;

    function renderBody() {
        if (loading) return <DotsLoader dotSize='1.25rem' className='section-card-loader' />;
        if (error != null) return <div className='section-card-error'>{error}</div>;
        if (empty) return <BolmateEmptyState className='section-card-empty' title={emptyLabel} />;
        return children;
    }

    function renderFooter() {
        if (loading || error != null || empty || !hasMore || !onLoadMore) return null;
        return (
            <div className='section-card-footer'>
                {/* Same props as the orders list's "load more" so both pages
                    show one button, not two variants of it. */}
                <BolmateButton
                    label={loadMoreLabel}
                    secondary
                    shape='rounded'
                    loading={loadingMore}
                    onClick={onLoadMore}
                />
            </div>
        );
    }

    return (
        <section className={classNames('bolmate-section-card', className)}>
            <BolmateListGroup title={title}>{renderBody()}</BolmateListGroup>
            {renderFooter()}
        </section>
    );
}
